"use client";
// ============================================================
// CyberAlem 2.0 — Course Studio (PHASE 6) frontend
// Автор (COURSE_AUTHOR/TEACHER/ADMIN) курсты кодқа қол тиместен
// құрады: modules → lessons → steps + 4 тілдік аудармалар.
// ============================================================
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  BookPlus,
  Check,
  ChevronDown,
  ChevronUp,
  Globe,
  LayoutTemplate,
  Plus,
  Send,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { api, post } from "@/lib/api/client";
import { useT, type Lang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth/provider";
import { Button } from "@/components/ui";

type StepType =
  | "THEORY"
  | "VIDEO"
  | "QUIZ_SINGLE"
  | "QUIZ_MULTI"
  | "SHORT_ANSWER"
  | "REPORT"
  | "LAB_TASK";

interface CStep {
  id: string;
  type: StepType;
  title: string;
  content: Record<string, unknown>;
}
interface CLesson {
  id: string;
  title: string;
  objective: string;
  minutes: number;
  xp: number;
  steps: CStep[];
}
interface CModule {
  id: string;
  title: string;
  lessons: CLesson[];
}
interface TrCell {
  fields: Record<string, unknown>;
  status: string;
}
type Translations = Partial<Record<Lang, Record<string, TrCell>>>;

interface CourseFull {
  id: string;
  owner_id: string;
  title: string;
  description: string;
  category: string;
  level: string;
  status: string;
  content: { modules: CModule[] };
  translations: Translations;
  updated_at: string;
}

const LOCALES: Lang[] = ["kk", "ru", "en", "ko"];
const TR_STATUSES = ["DRAFT", "AI_TRANSLATED", "HUMAN_REVIEWED", "APPROVED", "PUBLISHED"];
const STEP_TYPES: StepType[] = [
  "THEORY",
  "QUIZ_SINGLE",
  "QUIZ_MULTI",
  "SHORT_ANSWER",
  "VIDEO",
  "REPORT",
  "LAB_TASK",
];

function nid(prefix: string): string {
  return `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export function AuthorCoursesPage() {
  const t = useT();
  const router = useRouter();
  const { user } = useAuth();
  const [items, setItems] = useState<
    { id: string; title: string; status: string; lessons: number; updated_at: string }[]
  >([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");

  const load = useCallback(() => {
    api<{ id: string; title: string; status: string; lessons: number; updated_at: string }[]>(
      "/author/courses",
    )
      .then(setItems)
      .catch((e) => setError(String(e)));
  }, []);

  useEffect(load, [load]);

  const create = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const d = await post<{ id: string }>("/author/courses", { title: newTitle.trim() });
      router.push(`/my-courses/${d.id}`);
    } catch (e) {
      setError(String(e));
    } finally {
      setCreating(false);
    }
  };

  if (!user || !["COURSE_AUTHOR", "TEACHER", "ADMIN"].includes(user.role)) {
    return (
      <div className="page">
        <div className="empty-state">
          <p>{t("author.no_role")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-heading">
        <h1>{t("author.studio")}</h1>
        <p className="muted">{t("author.studio_desc")}</p>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="card studio-new">
        <input
          className="input"
          placeholder={t("author.new_ph")}
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && create()}
        />
        <Button onClick={create} disabled={creating || !newTitle.trim()}>
          <BookPlus size={16} /> {t("author.new")}
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="empty-state">
          <LayoutTemplate size={34} strokeWidth={1.3} />
          <p className="muted">{t("author.empty")}</p>
        </div>
      ) : (
        <div className="studio-list">
          {items.map((c) => (
            <Link key={c.id} href={`/my-courses/${c.id}`} className="studio-item card">
              <div className="between">
                <strong>{c.title}</strong>
                <span className={`badge badge-${c.status === "PUBLISHED" ? "ok" : "warn"}`}>
                  {c.status === "PUBLISHED" ? t("author.published") : t("author.draft")}
                </span>
              </div>
              <span className="muted small">
                {c.lessons} {t("author.lessons")} · {new Date(c.updated_at).toLocaleString()}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function AuthorCourseEditor({ id }: { id: string }) {
  const t = useT();
  const { user } = useAuth();
  const [course, setCourse] = useState<CourseFull | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [trLocale, setTrLocale] = useState<Lang>("ru");
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // PHASE 8: AI Course Assistant (draft-only)
  const [aiTopic, setAiTopic] = useState("");
  const [aiScope, setAiScope] = useState<"lesson" | "quiz">("lesson");
  const [aiDraft, setAiDraft] = useState<{
    scope: string;
    topic: string;
    lesson_title?: string;
    lesson_objective?: string;
    steps: { type: string; title: string; content: Record<string, unknown>; needs_review: string[] }[];
    disclaimer: string;
  } | null>(null);
  const [aiBusy, setAiBusy] = useState(false);

  const askAssistant = async () => {
    if (!aiTopic.trim()) return;
    setAiBusy(true);
    try {
      const d = await post<NonNullable<typeof aiDraft>>(
        `/author/courses/${id}/assistant`,
        { scope: aiScope, topic: aiTopic.trim() },
      );
      setAiDraft(d);
    } catch (e) {
      setError(String(e));
    } finally {
      setAiBusy(false);
    }
  };

  // Accept: мазмұн ағашына ЖАЗЫЛАДЫ (mұғалімнің нақты әрекеті)
  const acceptDraft = () => {
    if (!aiDraft) return;
    patchContent((c) => {
      const d = aiDraft!;
      const steps: CStep[] = d.steps.map((st) => ({
        id: nid("st-"),
        type: st.type as CStep["type"],
        title: st.title,
        content: st.content,
      }));
      if (d.scope === "lesson") {
        const modules = c.content.modules.length ? c.content.modules : [pushModule(c)];
        modules[modules.length - 1].lessons.push({
          id: nid("cl-"),
          title: d.lesson_title ?? d.topic,
          objective: d.lesson_objective ?? "",
          minutes: 15,
          xp: 50,
          steps,
        });
      } else {
        // quiz: соңғы сабаққа қосылады (жоқ болса — жаңа сабақ)
        const mods = c.content.modules;
        let lesson: CLesson | undefined;
        for (let i = mods.length - 1; i >= 0; i--) {
          const ls = mods[i].lessons;
          if (ls.length) { lesson = ls[ls.length - 1]; break; }
        }
        if (!lesson) {
          if (!mods.length) pushModule(c);
          const m = c.content.modules[c.content.modules.length - 1];
          lesson = { id: nid("cl-"), title: d.topic, objective: "", minutes: 15, xp: 50, steps: [] };
          m.lessons.push(lesson);
        }
        lesson.steps.push(...steps);
      }
    });
    setAiDraft(null);
    setAiTopic("");
  };

  const pushModule = (c: CourseFull): CModule => {
    const m: CModule = { id: nid("m-"), title: `Модуль ${c.content.modules.length + 1}`, lessons: [] };
    c.content.modules.push(m);
    return m;
  };

  const load = useCallback(() => {
    api<CourseFull>(`/author/courses/${id}`)
      .then(setCourse)
      .catch((e) => setError(String(e)));
  }, [id]);

  useEffect(load, [load]);

  const doSave = useCallback(
    async (next: CourseFull) => {
      setSaving(true);
      try {
        const d = await api<CourseFull>(`/author/courses/${id}`, {
          method: "PATCH",
          body: JSON.stringify({
            title: next.title,
            description: next.description,
            level: next.level,
            category: next.category,
            content: next.content,
            translations: next.translations,
          }),
        });
        setCourse(d);
        setSavedAt(new Date().toISOString());
      } catch (e) {
        setError(String(e));
      } finally {
        setSaving(false);
      }
    },
    [id],
  );

  // автосақтау: 800ms debounce (әр таңбаға PATCH шашпайды)
  const patchContent = useCallback(
    (fn: (c: CourseFull) => void) => {
      setCourse((prev) => {
        if (!prev) return prev;
        const next: CourseFull = JSON.parse(JSON.stringify(prev));
        fn(next);
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => void doSave(next), 800);
        return next;
      });
    },
    [doSave],
  );

  const setTranslationCell = useCallback(
    (key: string, fields: Record<string, unknown>, status?: string) => {
      setCourse((prev) => {
        if (!prev) return prev;
        const next: CourseFull = JSON.parse(JSON.stringify(prev));
        const loc = next.translations[trLocale] ?? {};
        const cell = loc[key] ?? { fields: {}, status: "DRAFT" };
        loc[key] = {
          fields: { ...cell.fields, ...fields },
          status: status ?? cell.status,
        };
        next.translations[trLocale] = loc;
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => void doSave(next), 800);
        return next;
      });
    },
    [doSave, trLocale],
  );

  const publish = async (published: boolean) => {
    try {
      await post(`/author/courses/${id}/${published ? "publish" : "unpublish"}`);
      load();
    } catch (e) {
      setError(String(e));
    }
  };

  const remove = async () => {
    if (!window.confirm(t("author.delete_q"))) return;
    try {
      await api(`/author/courses/${id}`, { method: "DELETE" });
      window.location.href = "/my-courses";
    } catch (e) {
      setError(String(e));
    }
  };

  if (!course) {
    return (
      <div className="page">
        {error ? <div className="alert alert-error">{error}</div> : <p className="muted">…</p>}
      </div>
    );
  }
  if (!user || !["COURSE_AUTHOR", "TEACHER", "ADMIN"].includes(user.role)) {
    return (
      <div className="page">
        <div className="empty-state">
          <p>{t("author.no_role")}</p>
        </div>
      </div>
    );
  }

  const tr = course.translations[trLocale] ?? {};
  const courseTr: TrCell = tr["course"] ?? { fields: {}, status: "DRAFT" };

  return (
    <div className="page studio">
      <div className="studio-head">
        <div>
          <Link href="/my-courses" className="muted small">← {t("author.back")}</Link>
          <h1>{course.title}</h1>
        </div>
        <div className="studio-actions">
          <span className={`badge badge-${course.status === "PUBLISHED" ? "ok" : "warn"}`}>
            {course.status === "PUBLISHED" ? t("author.published") : t("author.draft")}
          </span>
          {savedAt && <span className="muted small">{t("author.saved")} ✓</span>}
          {course.status !== "PUBLISHED" ? (
            <Button onClick={() => publish(true)}>
              <Send size={15} /> {t("author.publish")}
            </Button>
          ) : (
            <Button variant="ghost" onClick={() => publish(false)}>
              {t("author.unpublish")}
            </Button>
          )}
          <Button variant="ghost" onClick={remove} title={t("author.delete")}>
            <Trash2 size={15} />
          </Button>
        </div>
      </div>
      {error && <div className="alert alert-error">{error}</div>}

      <div className="studio-grid">
        {/* ═══ Контент (kk — түпнұсқа) ═══ */}
        <div className="studio-content">
          <div className="card">
            <h3>
              <Globe size={15} /> {t("author.source_tab")}
            </h3>
            <label className="field">
              <span>{t("author.title")}</span>
              <input
                className="input"
                value={course.title}
                onChange={(e) =>
                  patchContent((c) => {
                    c.title = e.target.value;
                  })
                }
              />
            </label>
            <label className="field">
              <span>{t("author.description")}</span>
              <textarea
                className="input"
                rows={2}
                value={course.description}
                onChange={(e) =>
                  patchContent((c) => {
                    c.description = e.target.value;
                  })
                }
              />
            </label>
            <div className="studio-meta">
              <label className="field">
                <span>{t("author.level")}</span>
                <select
                  className="input"
                  value={course.level}
                  onChange={(e) =>
                    patchContent((c) => {
                      c.level = e.target.value;
                    })
                  }
                >
                  <option value="BASIC">{t("author.level_basic")}</option>
                  <option value="MEDIUM">{t("author.level_medium")}</option>
                  <option value="ADVANCED">{t("author.level_advanced")}</option>
                </select>
              </label>
            </div>
          </div>

          {/* PHASE 8: AI Course Assistant (draft-only, Accept/Edit/Reject) */}
          <div className="card ai-panel">
            <h3>
              <Sparkles size={15} /> {t("assistant.title")}
            </h3>
            <div className="studio-new" style={{ margin: 0 }}>
              <input
                className="input"
                placeholder={t("assistant.topic_ph")}
                value={aiTopic}
                onChange={(e) => setAiTopic(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && askAssistant()}
              />
              <select
                className="input"
                value={aiScope}
                onChange={(e) => setAiScope(e.target.value as "lesson" | "quiz")}
                style={{ maxWidth: 190 }}
              >
                <option value="lesson">{t("assistant.scope_lesson")}</option>
                <option value="quiz">{t("assistant.scope_quiz")}</option>
              </select>
              <Button onClick={askAssistant} disabled={aiBusy || !aiTopic.trim()}>
                {t("assistant.generate")}
              </Button>
            </div>
            {aiDraft && (
              <div className="ai-draft">
                <p className="muted small ai-disc">{aiDraft.disclaimer}</p>
                {aiDraft.scope === "lesson" && (
                  <div className="ai-draft-head">
                    <strong>{aiDraft.lesson_title}</strong>
                    <span className="muted small">{aiDraft.lesson_objective}</span>
                  </div>
                )}
                {aiDraft.steps.map((st, i) => (
                  <div key={i} className="ai-step">
                    <span className={`badge badge-step-${st.type}`}>{st.type}</span>
                    <strong>{st.title}</strong>
                    {st.needs_review.length > 0 && (
                      <ul className="ai-review">
                        {st.needs_review.map((r, j) => (
                          <li key={j}>{r}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
                <div className="between" style={{ marginTop: 10 }}>
                  <Button onClick={acceptDraft}>
                    <Check size={15} /> {t("assistant.accept")}
                  </Button>
                  <Button variant="ghost" onClick={() => setAiDraft(null)}>
                    <X size={15} /> {t("assistant.reject")}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {course.content.modules.map((m, mi) => (
            <div key={m.id} className="card studio-module">
              <div className="between">
                <input
                  className="input studio-mod-title"
                  value={m.title}
                  onChange={(e) =>
                    patchContent((c) => {
                      c.content.modules[mi].title = e.target.value;
                    })
                  }
                />
                <button
                  className="icon-btn"
                  title={t("author.delete")}
                  onClick={() =>
                    patchContent((c) => {
                      c.content.modules.splice(mi, 1);
                    })
                  }
                >
                  <Trash2 size={15} />
                </button>
              </div>

              {m.lessons.map((l, li) => (
                <div key={l.id} className="studio-lesson">
                  <div className="between">
                    <input
                      className="input"
                      value={l.title}
                      onChange={(e) =>
                        patchContent((c) => {
                          c.content.modules[mi].lessons[li].title = e.target.value;
                        })
                      }
                    />
                    <button
                      className="icon-btn"
                      title={t("author.delete")}
                      onClick={() =>
                        patchContent((c) => {
                          c.content.modules[mi].lessons.splice(li, 1);
                        })
                      }
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <input
                    className="input small-input"
                    placeholder={t("author.objective_ph")}
                    value={l.objective}
                    onChange={(e) =>
                      patchContent((c) => {
                        c.content.modules[mi].lessons[li].objective = e.target.value;
                      })
                    }
                  />

                  {l.steps.map((st, si) => (
                    <StepEditor
                      key={st.id}
                      step={st}
                      onChange={(fn) =>
                        patchContent((c) => {
                          fn(c.content.modules[mi].lessons[li].steps[si]);
                        })
                      }
                      onDelete={() =>
                        patchContent((c) => {
                          c.content.modules[mi].lessons[li].steps.splice(si, 1);
                        })
                      }
                      onMove={(dir) =>
                        patchContent((c) => {
                          const arr = c.content.modules[mi].lessons[li].steps;
                          const j = si + dir;
                          if (j < 0 || j >= arr.length) return;
                          [arr[si], arr[j]] = [arr[j], arr[si]];
                        })
                      }
                    />
                  ))}

                  <div className="studio-add-step">
                    <select
                      className="input"
                      defaultValue=""
                      onChange={(e) => {
                        const type = e.target.value as StepType;
                        if (!type) return;
                        e.target.value = "";
                        patchContent((c) => {
                          const lesson = c.content.modules[mi].lessons[li];
                          lesson.steps.push({
                            id: nid("st-"),
                            type,
                            title: `Қадам ${lesson.steps.length + 1}`,
                            content: defaultContent(type),
                          });
                        });
                      }}
                    >
                      <option value="" disabled>
                        + {t("author.add_step")}
                      </option>
                      {STEP_TYPES.map((s) => (
                        <option key={s} value={s}>
                          {t(`author.step_${s}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}

              <button
                className="linklike"
                onClick={() =>
                  patchContent((c) => {
                    c.content.modules[mi].lessons.push({
                      id: nid("cl-"),
                      title: `Сабақ ${c.content.modules[mi].lessons.length + 1}`,
                      objective: "",
                      minutes: 15,
                      xp: 50,
                      steps: [],
                    });
                  })
                }
              >
                + {t("author.add_lesson")}
              </button>
            </div>
          ))}

          <Button
            variant="ghost"
            onClick={() =>
              patchContent((c) => {
                c.content.modules.push({
                  id: nid("m-"),
                  title: `Модуль ${c.content.modules.length + 1}`,
                  lessons: [],
                });
              })
            }
          >
            <Plus size={15} /> {t("author.add_module")}
          </Button>
        </div>

        {/* ═══ Аудармалар (ru/en/ko) ═══ */}
        <div className="studio-trans">
          <div className="card">
            <div className="between" style={{ marginBottom: 10 }}>
              <h3 style={{ margin: 0 }}>
                <Globe size={15} /> {t("author.translations")}
              </h3>
              <div className="terminal-tabs">
                {LOCALES.filter((l) => l !== "kk").map((l) => (
                  <Button
                    key={l}
                    variant={trLocale === l ? "primary" : "ghost"}
                    onClick={() => setTrLocale(l)}
                  >
                    {l.toUpperCase()}
                  </Button>
                ))}
              </div>
            </div>

            {/* course level */}
            <div className="tr-cell">
              <span className="tr-key">course</span>
              <input
                className="input"
                placeholder={t("author.title")}
                value={(courseTr.fields.title as string | undefined) ?? ""}
                onChange={(e) => setTranslationCell("course", { title: e.target.value })}
              />
              <input
                className="input"
                placeholder={t("author.description")}
                value={(courseTr.fields.description as string | undefined) ?? ""}
                onChange={(e) =>
                  setTranslationCell("course", { description: e.target.value })
                }
              />
              <StatusSelect
                value={courseTr.status}
                onChange={(st) => setTranslationCell("course", {}, st)}
              />
            </div>

            {course.content.modules.map((m, mi) => {
              const key = `module:${m.id}`;
              const cell: TrCell = tr[key] ?? { fields: {}, status: "DRAFT" };
              return (
                <div key={m.id} className="tr-block">
                  <div className="tr-cell">
                    <span className="tr-key">{m.title}</span>
                    <input
                      className="input"
                      placeholder={t("author.module_title")}
                      value={(cell.fields.title as string | undefined) ?? ""}
                      onChange={(e) => setTranslationCell(key, { title: e.target.value })}
                    />
                    <StatusSelect
                      value={cell.status}
                      onChange={(st) => setTranslationCell(key, {}, st)}
                    />
                  </div>
                  {m.lessons.map((l, li) => {
                    const lkey = `lesson:${l.id}`;
                    const lcell: TrCell = tr[lkey] ?? { fields: {}, status: "DRAFT" };
                    return (
                      <div key={l.id} className="tr-sub">
                        <div className="tr-cell">
                          <span className="tr-key">{l.title}</span>
                          <input
                            className="input"
                            placeholder={t("author.lesson_title")}
                            value={(lcell.fields.title as string | undefined) ?? ""}
                            onChange={(e) =>
                              setTranslationCell(lkey, { title: e.target.value })
                            }
                          />
                          <StatusSelect
                            value={lcell.status}
                            onChange={(st) => setTranslationCell(lkey, {}, st)}
                          />
                        </div>
                        {l.steps.map((st) => {
                          const skey = `step:${st.id}`;
                          const scell: TrCell = tr[skey] ?? { fields: {}, status: "DRAFT" };
                          const q =
                            (scell.fields.question as string | undefined) ??
                            (scell.fields.text as string | undefined) ??
                            "";
                          return (
                            <div key={st.id} className="tr-cell tr-step">
                              <span className="tr-key small">
                                {st.type}: {st.title}
                              </span>
                              <input
                                className="input"
                                placeholder={t("author.step_title")}
                                value={(scell.fields.title as string | undefined) ?? ""}
                                onChange={(e) =>
                                  setTranslationCell(skey, { title: e.target.value })
                                }
                              />
                              {(st.type === "QUIZ_SINGLE" || st.type === "QUIZ_MULTI") && (
                                <input
                                  className="input"
                                  placeholder={t("author.question")}
                                  value={q}
                                  onChange={(e) =>
                                    setTranslationCell(skey, { question: e.target.value })
                                  }
                                />
                              )}
                              {st.type === "THEORY" && (
                                <textarea
                                  className="input"
                                  rows={3}
                                  placeholder={t("author.theory_text")}
                                  value={q}
                                  onChange={(e) =>
                                    setTranslationCell(skey, { text: e.target.value })
                                  }
                                />
                              )}
                              <StatusSelect
                                value={scell.status}
                                onChange={(stt) => setTranslationCell(skey, {}, stt)}
                              />
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            <p className="muted small">{t("author.translations_hint")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusSelect({ value, onChange }: { value: string; onChange: (s: string) => void }) {
  return (
    <select className="input status-select" value={value} onChange={(e) => onChange(e.target.value)}>
      {TR_STATUSES.map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
    </select>
  );
}

function defaultContent(type: StepType): Record<string, unknown> {
  switch (type) {
    case "THEORY":
      return { text: "" };
    case "VIDEO":
      return { url: "" };
    case "QUIZ_SINGLE":
    case "QUIZ_MULTI":
      return {
        question: "",
        options: ["", "", "", ""].map((_, i) => (i === 0 ? "" : "")),
        answer: type === "QUIZ_SINGLE" ? 0 : [0],
        explanation: "",
      };
    case "SHORT_ANSWER":
      return { question: "", check: { kind: "contains", fragments: [] } };
    case "REPORT":
      return { question: "" };
    case "LAB_TASK":
      return { text: "" };
  }
}

function StepEditor({
  step,
  onChange,
  onDelete,
  onMove,
}: {
  step: CStep;
  onChange: (fn: (s: CStep) => void) => void;
  onDelete: () => void;
  onMove: (dir: -1 | 1) => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const c = step.content;

  return (
    <div className="studio-step">
      <div className="between studio-step-head">
        <button className="linklike" onClick={() => setOpen(!open)}>
          {open ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          <span className={`badge badge-step-${step.type}`}>{step.type}</span>
          {step.title}
        </button>
        <span className="studio-step-actions">
          <button className="icon-btn" title="↑" onClick={() => onMove(-1)}>
            <ChevronUp size={14} />
          </button>
          <button className="icon-btn" title="↓" onClick={() => onMove(1)}>
            <ChevronDown size={14} />
          </button>
          <button className="icon-btn" title={t("author.delete")} onClick={onDelete}>
            <X size={14} />
          </button>
        </span>
      </div>

      {open && (
        <div className="studio-step-body">
          <input
            className="input"
            value={step.title}
            onChange={(e) => onChange((s) => (s.title = e.target.value))}
          />
          {step.type === "THEORY" && (
            <textarea
              className="input"
              rows={4}
              value={(c.text as string) ?? ""}
              onChange={(e) => onChange((s) => (s.content.text = e.target.value))}
            />
          )}
          {step.type === "VIDEO" && (
            <input
              className="input"
              placeholder="https://…"
              value={(c.url as string) ?? ""}
              onChange={(e) => onChange((s) => (s.content.url = e.target.value))}
            />
          )}
          {(step.type === "QUIZ_SINGLE" || step.type === "QUIZ_MULTI") && (
            <>
              <input
                className="input"
                placeholder={t("author.question")}
                value={(c.question as string) ?? ""}
                onChange={(e) => onChange((s) => (s.content.question = e.target.value))}
              />
              {((c.options as string[]) ?? []).map((o, i) => (
                <div key={i} className="between">
                  <input
                    className="input"
                    placeholder={`variant ${i + 1}`}
                    value={o}
                    onChange={(e) =>
                      onChange((s) => {
                        const opts = (s.content.options as string[]) ?? [];
                        opts[i] = e.target.value;
                        s.content.options = opts;
                      })
                    }
                  />
                  {step.type === "QUIZ_SINGLE" ? (
                    <button
                      className={`icon-btn ${(c.answer as number) === i ? "correct-mark" : ""}`}
                      title={t("author.correct")}
                      onClick={() => onChange((s) => (s.content.answer = i))}
                    >
                      <Check size={14} />
                    </button>
                  ) : (
                    <button
                      className={`icon-btn ${(c.answer as number[]).includes(i) ? "correct-mark" : ""}`}
                      title={t("author.correct")}
                      onClick={() =>
                        onChange((s) => {
                          const ans = (s.content.answer as number[]) ?? [];
                          s.content.answer = ans.includes(i)
                            ? ans.filter((x) => x !== i)
                            : [...ans, i];
                        })
                      }
                    >
                      <Check size={14} />
                    </button>
                  )}
                </div>
              ))}
              <input
                className="input"
                placeholder={t("author.explanation")}
                value={(c.explanation as string) ?? ""}
                onChange={(e) => onChange((s) => (s.content.explanation = e.target.value))}
              />
            </>
          )}
          {step.type === "SHORT_ANSWER" && (
            <>
              <input
                className="input"
                placeholder={t("author.question")}
                value={(c.question as string) ?? ""}
                onChange={(e) => onChange((s) => (s.content.question = e.target.value))}
              />
              {(() => {
                const check = (c.check as { kind?: string; value?: string; fragments?: string[] }) ??
                  {};
                return (
                  <>
                    <div className="between">
                      <select
                        className="input"
                        value={check.kind ?? "contains"}
                        onChange={(e) =>
                          onChange((s) => {
                            s.content.check = {
                              kind: e.target.value,
                              value: "",
                              fragments: [],
                            };
                          })
                        }
                      >
                        <option value="contains">{t("author.check_contains")}</option>
                        <option value="literal">{t("author.check_literal")}</option>
                      </select>
                    </div>
                    {check.kind === "literal" ? (
                      <input
                        className="input"
                        placeholder={t("author.literal_value")}
                        value={check.value ?? ""}
                        onChange={(e) =>
                          onChange((s) => {
                            const ch = s.content.check as Record<string, unknown>;
                            ch.value = e.target.value;
                          })
                        }
                      />
                    ) : (
                      <input
                        className="input"
                        placeholder={t("author.fragments_ph")}
                        value={(check.fragments ?? []).join(", ")}
                        onChange={(e) =>
                          onChange((s) => {
                            const ch = s.content.check as Record<string, unknown>;
                            ch.fragments = e.target.value
                              .split(",")
                              .map((x) => x.trim())
                              .filter(Boolean);
                          })
                        }
                      />
                    )}
                  </>
                );
              })()}
            </>
          )}
          {step.type === "REPORT" && (
            <input
              className="input"
              placeholder={t("author.report_q")}
              value={(c.question as string) ?? ""}
              onChange={(e) => onChange((s) => (s.content.question = e.target.value))}
            />
          )}
          {step.type === "LAB_TASK" && (
            <p className="muted small">{t("author.lab_task_hint")}</p>
          )}
        </div>
      )}
    </div>
  );
}
